import { isMixedReviewEnabled } from "./revisionV2Api";

export const REVISION_RAPIDA_SALUD_ROLES = Object.freeze([1, 2, 6, 8, 10, 11]);
export const REVISION_RAPIDA_SALUD_MANAGER_ROLES = Object.freeze([1, 2, 10]);

const toRole = (user) => Number(user?.rol);

export const getStoredRevisionUser = () => {
  try {
    return JSON.parse(localStorage.getItem("token")) || null;
  } catch (error) {
    return null;
  }
};

export const canAccessRevisionRapidaSalud = (user) =>
  REVISION_RAPIDA_SALUD_ROLES.includes(toRole(user)) || (isMixedReviewEnabled() && toRole(user) === 9);

export const canManageRevisionRapidaSalud = (user) =>
  REVISION_RAPIDA_SALUD_MANAGER_ROLES.includes(toRole(user));

export const buildRevisionRapidaSaludPath = (convocatoriaId) =>
  `/revision-rapida-salud/${encodeURIComponent(String(convocatoriaId))}`;

export const buildRevisionRapidaSaludRecordsPath = (convocatoriaId) =>
  `${buildRevisionRapidaSaludPath(convocatoriaId)}/registros`;

export const buildRevisionRapidaSaludRecordPath = (convocatoriaId, recordId) =>
  `${buildRevisionRapidaSaludRecordsPath(convocatoriaId)}/${encodeURIComponent(String(recordId))}`;

export const REVISION_RECORD_STATUSES = Object.freeze({
  0: { code: 0, key: "PENDING", label: "Pendiente", color: "warning" },
  1: { code: 1, key: "VERIFIED", label: "Verificado", color: "success" },
  2: { code: 2, key: "UNCLASSIFIED", label: "Sin clasificar", color: "default" },
  3: { code: 3, key: "ERROR", label: "Error", color: "error" },
  4: { code: 4, key: "REMOVED", label: "Eliminado lógico", color: "default" },
});

export const getRevisionRecordStatus = (record) => {
  const statusValue = record?.status?.code ?? record?.status;
  const code = Number(statusValue);
  return REVISION_RECORD_STATUSES[code] || {
    code: Number.isNaN(code) ? null : code,
    key: record?.status?.key || "UNKNOWN",
    label: "Estado desconocido",
    color: "default",
  };
};

export const normalizeRevisionRecordSearch = (value) => {
  const trimmed = String(value || "").trim().slice(0, 100);
  return trimmed.length >= 2 ? trimmed : "";
};

export const getRevisionRecordActionLabel = (record) =>
  record?.source === "LEGACY" && record?.capabilities?.canEdit === true ? "Revisar" : "Ver";

export const extractRevisionRecordsError = (error) => {
  const mapped = extractRevisionApiError(error);

  if (mapped.status === 400) {
    return { ...mapped, message: "Revisa los filtros o la búsqueda e intenta nuevamente." };
  }

  if ([401, 404].includes(mapped.status)) return mapped;

  return { ...mapped, message: "No fue posible cargar los registros. Intenta nuevamente." };
};

export const readBackendCapability = (payload, capability) => {
  const capabilities = payload?.capabilities;

  if (Array.isArray(capabilities)) {
    return capabilities.includes(capability);
  }

  if (capabilities && typeof capabilities === "object") {
    if (Object.prototype.hasOwnProperty.call(capabilities, capability)) {
      return capabilities[capability] === true;
    }

    const camelCaseCapability = capability
      .toLowerCase()
      .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    if (Object.prototype.hasOwnProperty.call(capabilities, camelCaseCapability)) {
      return capabilities[camelCaseCapability] === true;
    }
  }

  return null;
};

export const resolveUxCapability = ({ payloads = [], capability, user }) => {
  for (const payload of payloads) {
    const backendDecision = readBackendCapability(payload, capability);
    if (backendDecision !== null) return backendDecision;
  }

  return canManageRevisionRapidaSalud(user);
};

export const extractRevisionApiError = (error) => {
  const status = Number(error?.response?.status || 0);
  const code = error?.response?.data?.error?.code || "";

  if (status === 401 || code === "AUTH_REQUIRED" || code === "INVALID_TOKEN") {
    return {
      status,
      code,
      message: "Tu sesión no es válida o ha expirado. Inicia sesión nuevamente.",
    };
  }

  if (status === 404 || code === "RESOURCE_NOT_FOUND") {
    return {
      status,
      code,
      message: "No tienes acceso a esta convocatoria o ya no está disponible.",
    };
  }

  if (status === 409 || code === "VERSION_CONFLICT") {
    return {
      status,
      code,
      message: "Los datos fueron actualizados por otro usuario. Recarga e intenta nuevamente.",
    };
  }

  if (status === 400 || code === "INVALID_REQUEST" || code === "INVALID_URL") {
    return {
      status,
      code,
      message: error?.response?.data?.error?.message || "Revisa los datos ingresados e intenta nuevamente.",
    };
  }

  return {
    status,
    code,
    message: "No fue posible completar la solicitud. Intenta nuevamente.",
  };
};

export const getTranscriptionPercent = (summary) => {
  const backendPercent = summary?.progress?.transcriptionPercent;
  if (backendPercent !== null && backendPercent !== undefined) {
    return Number(backendPercent);
  }

  const expected = Number(summary?.target?.expected);
  const transcribed = Number(summary?.progress?.transcribed);
  return expected > 0 ? (transcribed / expected) * 100 : null;
};

export const isHttpsUrl = (value) => {
  try {
    return new URL(value).protocol === "https:";
  } catch (error) {
    return false;
  }
};
